import type {
  CreateMeetingBody,
  CreateMeetingResponse,
  MeetingView,
  RejectReason,
} from '@sorted/types'

/**
 * Typed client over the API. Same origin - the app is served by Fastify, so
 * there is no base URL, no CORS and no preflight (build-spec §1). That matters
 * on the one screen that decides the funnel: Bob is in a WhatsApp webview on
 * mobile data, and every round trip is a chance to lose him.
 */

/**
 * The area under the pin.
 *
 * Only the id travels back to the server on create — it re-resolves it rather
 * than trusting a position from a browser. The rest is for display, and every
 * field but the id may be empty: OSM has no name for the middle of a lake, and
 * the pin is still somewhere you can meet.
 */
export interface Area {
  id: string
  postcode: string
  label: string
  country: string
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
  ) {
    super(code)
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // Only declare JSON when there is actually JSON to send. Fastify rejects a
  // bodyless request that claims `application/json` with a 400, which broke
  // /join outright: the recipient row was never created, no role cookie was
  // set, and the following /origin call then 403'd as "not the recipient".
  const headers: Record<string, string> = { ...(init?.headers as Record<string, string>) }
  if (init?.body !== undefined) headers['Content-Type'] = 'application/json'

  const res = await fetch(`/api${path}`, {
    ...init,
    headers,
    // The role cookie is the only way the server can tell Alice from Bob.
    credentials: 'same-origin',
  })

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string }
    throw new ApiError(res.status, body.error ?? 'request_failed')
  }
  return (await res.json()) as T
}

export const api = {
  create(body: CreateMeetingBody) {
    return request<CreateMeetingResponse>('/meetings', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  },

  /**
   * What is at this point, for labelling a dropped pin.
   *
   * The one endpoint not bounded by invariant 7, deliberately: it fires as the
   * map moves, so it had to be free. Photon over OpenStreetMap is unmetered,
   * so there is no per-call cost to bound.
   */
  areaAt(point: { lat: number; lng: number }) {
    return request<{ area: Area | null }>(
      `/areas/at?lat=${point.lat}&lng=${point.lng}`,
    ).then((r) => r.area)
  },

  join(token: string) {
    return request<{ role: 'creator' | 'recipient' }>(`/meetings/${token}/join`, {
      method: 'POST',
    })
  },

  submitOrigin(token: string, origin: { lat: number; lng: number }, label?: string) {
    return request<MeetingView>(`/meetings/${token}/origin`, {
      method: 'POST',
      body: JSON.stringify({ origin, ...(label ? { originLabel: label } : {}) }),
    })
  },

  get(token: string) {
    return request<MeetingView>(`/meetings/${token}`)
  },

  reject(token: string, reason?: RejectReason) {
    return request<MeetingView>(`/meetings/${token}/reject`, {
      method: 'POST',
      body: JSON.stringify(reason ? { reason } : {}),
    })
  },

  goBack(token: string) {
    return request<MeetingView>(`/meetings/${token}/back`, { method: 'POST' })
  },

  /** `placeId` names WHICH of the visible options - three are shown. */
  accept(token: string, placeId?: string) {
    return request<MeetingView>(`/meetings/${token}/accept`, {
      method: 'POST',
      ...(placeId ? { body: JSON.stringify({ placeId }) } : {}),
    })
  },

  /** Kill a link the creator decided against before sending. */
  discard(token: string) {
    return request<{ status: string }>(`/meetings/${token}/discard`, { method: 'POST' })
  },

  /**
   * The recipient's way out - same endpoint, either role may end it.
   *
   * The reason is optional and is the only place one is now collected: asked
   * as he leaves ("why weren't these what you expected?"), not under the cards
   * where it read as "show me others".
   */
  optOutRecipient(token: string, reason?: RejectReason) {
    return request<MeetingView>(`/meetings/${token}/opt-out`, {
      method: 'POST',
      body: JSON.stringify(reason ? { reason } : {}),
    })
  },

  optOut(token: string) {
    return request<{ status: string; handoffText: string }>(`/meetings/${token}/opt-out`, {
      method: 'POST',
    })
  },
}
